import { Children } from "react";
import i18n from "i18next";
import { useTranslation } from "react-i18next";

const VALUE_PART_SEPARATOR = " / ";

// وصل کردن چند تکه متن با /  (None و خالی‌ها حذف می‌شوند)
export function joinParts(parts, separator = " / ") {
  return parts
    .filter(
      (part) =>
        part !== undefined &&
        part !== null &&
        part !== "" &&
        part !== "None" &&
        part !== "none"
    )
    .join(separator);
}

// ساخت مقدار Hardware (ممکن است چند خط باشد)
export function formatHardware(primary, ...secondaryParts) {
  const cleanPrimary =
    primary && primary !== "None" && primary !== "none" ? primary : null;
  const secondary = joinParts(secondaryParts);
  if (!cleanPrimary && !secondary) return null;
  if (cleanPrimary && secondary) return [cleanPrimary, secondary];
  return cleanPrimary || secondary;
}

function toLocaleDigits(text) {
  if (i18n.language !== "fa" || text == null) return text;
  const persianDigits = "۰۱۲۳۴۵۶۷۸۹";
  return String(text).replace(/\d/g, (d) => persianDigits[Number(d)]);
}

export function formatQuantity(qty, isFa) {
  if (qty == null || qty === "") return null;
  if (isFa) return new Intl.NumberFormat("fa-IR").format(Number(qty));
  return String(qty);
}

function translateString(t, str) {
  const trimmed = String(str).trim();
  if (!trimmed) return null;
  if (trimmed.includes(VALUE_PART_SEPARATOR)) {
    return trimmed
      .split(VALUE_PART_SEPARATOR)
      .map((part) => translateString(t, part))
      .filter((part) => part != null && part !== "")
      .join(VALUE_PART_SEPARATOR);
  }
  return t(trimmed);
}

function translateValue(t, value) {
  if (value === undefined || value === null || value === "") return null;
  if (Array.isArray(value)) {
    const lines = value.map((line) => translateValue(t, line)).filter(Boolean);
    return lines.length ? lines : null;
  }
  const translated = translateString(t, value);
  return translated ? toLocaleDigits(translated) : null;
}

// یک ردیف: لیبل | مقدار  (اگر مقدار خالی باشد چیزی نشان نمی‌دهد)
export function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  const lines = Array.isArray(value) ? value.filter(Boolean) : null;
  if (lines && !lines.length) return null;

  return (
    <div className="orders-product-row">
      <span className="orders-product-label">{label}</span>
      <span className="orders-product-value">
        {lines
          ? lines.map((line, index) => (
              <span key={index} className="orders-product-value-line">
                {toLocaleDigits(line)}
              </span>
            ))
          : toLocaleDigits(value)}
      </span>
    </div>
  );
}

// یک بخش با عنوان (مثل General Details)
export function DetailSection({ title, children }) {
  const items = Children.toArray(children).filter(Boolean);
  if (!items.length) return null;

  return (
    <div className="orders-product-section">
      {title && <h3 className="orders-product-section-title">{title}</h3>}
      <div className="orders-product-section-body">{items}</div>
    </div>
  );
}

// ---------- کمک‌کننده‌های ساده ----------

const DRAPERY_MODELS = ["0099", "0321"];

const DRAPERY_MOUNT = {
  4801: "Ceiling",
  4802: "Wall",
  4803: "Moulding",
};

const SHADE_MOUNT = {
  4801: "Inside",
  4802: "Outside",
  4803: "Moulding",
};

const CURTAIN_POSITION = {
  4901: "Standard",
  4902: "Wall to Wall",
  4903: "Right Corner Window",
  4904: "Left Corner Window",
};

const FINISHED_LENGTH_TYPE = {
  5401: "Sill",
  5402: "Apron",
  5403: "Floor",
  5404: "Slight Puddle",
};

function isDraperyModel(product) {
  return DRAPERY_MODELS.includes(`${product?.SewingModelId || ""}`);
}

function getMount(product, t) {
  const drapery = isDraperyModel(product);

  let mount = product.Mount;
  if (!mount && product.MountPositionId != null) {
    mount = drapery
      ? DRAPERY_MOUNT[product.MountPositionId]
      : SHADE_MOUNT[product.MountPositionId];
  }
  if (!mount) return null;

  // پرده‌های لایه‌ای: Ceiling / Wall / Moulding
  if (drapery) {
    return translateValue(t, mount);
  }

  // سایه‌بان‌ها: Outside / Wall Mount و ...
  let outsideType = product.installationLocation;
  if (!outsideType || outsideType === "undefined") {
    if (product.IsWalled === true || product.IsWalled === "true") {
      outsideType = "Wall";
    } else if (product.IsWalled === false || product.IsWalled === "false") {
      outsideType = "Ceiling";
    } else if (typeof product.IsWalled === "string" && product.IsWalled) {
      outsideType = product.IsWalled;
    } else {
      outsideType = null;
    }
  }

  if (mount === "Inside") {
    return t("Inside");
  }
  if (mount === "Moulding") {
    return t("Moulding Mount");
  }
  if (outsideType === "Wall") {
    return t("TO DETERMINE OUTSIDE MOUNT_WALL");
  }
  if (outsideType === "Ceiling") {
    return t("TO DETERMINE OUTSIDE MOUNT CEILING");
  }
  if (mount === "Outside") {
    return t("Outside");
  }

  return translateValue(t, mount);
}

function getCurtainPosition(product) {
  if (product.CurtainPosition) return product.CurtainPosition;
  return CURTAIN_POSITION[product.CurtainPositionId] || null;
}

function getFinishedLengthType(product) {
  // اگر CalcWindowSize برابر false باشد، نشان داده نشود
  const calcWindowSize = product.calcMeasurements ?? product.CalcWindowSize;
  if (calcWindowSize === false || calcWindowSize === "false") {
    return null;
  }

  if (product.FinishedLengthType) return product.FinishedLengthType;
  return FINISHED_LENGTH_TYPE[product.FinishedLengthTypeId] || null;
}

function getChainLength(value, t) {
  if (value === undefined || value === null || value === "") return null;
  const text = String(value).trim();
  // اگر عدد است (مثل 250) واحد cm اضافه شود
  if (/^\d+(\.\d+)?$/.test(text)) {
    const number =
      i18n.language === "fa"
        ? new Intl.NumberFormat("fa-IR").format(Number(text))
        : text;
    return number + t("cm");
  }
  return translateValue(t, text);
}

function getMotorChannels(value) {
  if (value === undefined || value === null || value === "") return null;
  let result;
  if (Array.isArray(value)) {
    const parts = value.filter((item) => item !== undefined && item !== null && item !== "");
    if (!parts.length) return null;
    result = parts.join(",");
  } else {
    result = String(value);
  }
  return toLocaleDigits(result);
}

function getFabricColor(product, isFa) {
  return joinParts([
    isFa ? product.FabricDesignFa : product.FabricDesignEn,
    isFa ? product.FabricColorFa : product.FabricColorEn,
  ]);
}

function getFabricColor2(product, isFa) {
  return joinParts([
    isFa ? product.FabricDesignFa2 : product.FabricDesignEn2,
    isFa ? product.FabricColorFa2 : product.FabricColorEn2,
  ]);
}

function getSheerFabricColor(product, isFa) {
  return joinParts([
    isFa ? product.SheersDesignFa : product.SheersDesignEn,
    isFa ? product.SheersColorFa : product.SheersColorEn,
  ]);
}

function getDraperyHardware(product, isFa) {
  return formatHardware(
    (isFa ? product.RailDesignFaA : product.RailDesignEnA) ||
      product.hardwareDrapery ||
      product.Hardware,
    isFa ? product.RailColorFaA : product.RailColorEnA,
    product.BatonOptionA
  );
}

function getSheerHardware(product, isFa) {
  return formatHardware(
    (isFa ? product.RailDesignFaB : product.RailDesignEnB) || product.hardwareSheer,
    isFa ? product.RailColorFaB : product.RailColorEnB,
    product.BatonOptionB || product.ControlPositionB
  );
}

function getPrivacyHardware(product, isFa) {
  return formatHardware(
    (isFa ? product.RailDesignFaC : product.RailDesignEnC) || product.hardwarePrivacyLayer,
    isFa ? product.RailColorFaC : product.RailColorEnC,
    product.BatonOptionC || product.ControlPositionC
  );
}

function getControlType(product, t) {
  if (product.ControlType === "Motorized" && product.MotorType) {
    return t(product.ControlType) + " / " + translateValue(t, product.MotorType);
  }
  return translateValue(t, product.ControlType);
}

function AccessoriesSection({ product, isFa, t }) {
  const accessories = Array.isArray(product.Accessories)
    ? product.Accessories.filter((item) => item.DesignNameEn || item.DesignNameFa)
    : [];
  if (!accessories.length) return null;

  return (
    <DetailSection title={t("Accessories")}>
      {accessories.map((item, index) => (
        <DetailRow
          key={index}
          label={joinParts([
            isFa ? item.DesignNameFa : item.DesignNameEn,
            isFa ? item.ColorNameFa : item.ColorNameEn,
          ])}
          value={
            item.Qty != null ? t("agree_Qty") + formatQuantity(item.Qty, isFa) : null
          }
        />
      ))}
    </DetailSection>
  );
}

// ---------- جزئیات هر مدل (ترتیب از UserProjects) ----------

// مدل‌های پرده لایه‌ای: 0099 و 0321
function Details0099And0321({ product, isFa, t }) {
  const hasSheer = !!(product.SheerHeaderStyle || product.PanelTypeB || product.FabricDesignEn2);
  const hasPrivacy = !!(product.SheersDesignEn || product.SheersId1);

  return (
    <>
      <DetailSection title={t("General Details")}>
        <DetailRow label={t("Mount Position")} value={getMount(product, t)} />
        <DetailRow
          label={t("Curtain Position")}
          value={translateValue(t, getCurtainPosition(product))}
        />
        <DetailRow
          label={t("Finished Length Type")}
          value={translateValue(t, getFinishedLengthType(product))}
        />
      </DetailSection>

      <DetailSection title={t("Drapery")}>
        <DetailRow label={t("Fabric & Color")} value={getFabricColor(product, isFa)} />
        <DetailRow label={t("Lining")} value={translateValue(t, product.Lining)} />
        <DetailRow label={t("Panel Coverage")} value={translateValue(t, product.PanelCoverage)} />
        <DetailRow label={t("Panel Type")} value={translateValue(t, product.PanelTypeA)} />
        <DetailRow label={t("Grommet Finish")} value={translateValue(t, product.GrommetFinish)} />
        <DetailRow
          label={t("Hardware")}
          value={translateValue(t, getDraperyHardware(product, isFa))}
        />
        {product.HandCurtainNum > 0 && (
          <DetailRow
            label={t("Matching Tieback")}
            value={t("agree_Qty") + formatQuantity(product.HandCurtainNum, isFa)}
          />
        )}
      </DetailSection>

      {hasSheer && (
        <DetailSection title={t("Sheer")}>
          <DetailRow label={t("Fabric & Color")} value={getFabricColor2(product, isFa)} />
          <DetailRow label={t("Lead Weight")} value={translateValue(t, product.hasLead)} />
          <DetailRow
            label={t("Header Style")}
            value={translateValue(t, product.SheerHeaderStyle)}
          />
          <DetailRow label={t("Panel Type")} value={translateValue(t, product.PanelTypeB)} />
          <DetailRow
            label={t("Hardware")}
            value={translateValue(t, getSheerHardware(product, isFa))}
          />
          {product.HandCurtainNum2 > 0 && (
            <DetailRow
              label={t("Matching Tieback")}
              value={t("agree_Qty") + formatQuantity(product.HandCurtainNum2, isFa)}
            />
          )}
          <DetailRow
            label={t("Motor Position")}
            value={translateValue(t, product.MotorPositionB)}
          />
          <DetailRow label={t("Remote Name")} value={product.RemoteNameB} />
          <DetailRow
            label={t("Motor Channels")}
            value={getMotorChannels(product.MotorChannelsB)}
          />
        </DetailSection>
      )}

      {hasPrivacy && (
        <DetailSection title={t("Privacy Layer")}>
          <DetailRow label={t("Fabric & Color")} value={getSheerFabricColor(product, isFa)} />
          <DetailRow
            label={t("Header Style")}
            value={translateValue(t, product.PrivacyLayerHeaderStyle)}
          />
          <DetailRow label={t("Panel Type")} value={translateValue(t, product.PanelTypeC)} />
          <DetailRow
            label={t("Hardware")}
            value={translateValue(t, getPrivacyHardware(product, isFa))}
          />
          <DetailRow
            label={t("Motor Position")}
            value={translateValue(t, product.MotorPositionC)}
          />
          <DetailRow label={t("Remote Name")} value={product.RemoteNameC} />
          <DetailRow
            label={t("Motor Channels")}
            value={getMotorChannels(product.MotorChannelsC)}
          />
        </DetailSection>
      )}

      <AccessoriesSection product={product} isFa={isFa} t={t} />
    </>
  );
}

// Dual Roller: 0325
function Details0325({ product, isFa, t }) {
  return (
    <>
      <DetailSection title={t("General Details")}>
        <DetailRow
          label={t("dualRoller_step1")}
          value={getFabricColor(product, isFa)}
        />
        <DetailRow
          label={t("dualRoller_step2")}
          value={getFabricColor2(product, isFa)}
        />
        <DetailRow label={t("Mount Position")} value={getMount(product, t)} />
        <DetailRow label={t("Control Type")} value={getControlType(product, t)} />
        <DetailRow
          label={t("Motor Position")}
          value={translateValue(t, product.MotorPosition)}
        />
        <DetailRow label={t("Remote Name")} value={product.RemoteName} />
        <DetailRow
          label={t("Motor Channels")}
          value={getMotorChannels(product.MotorChannels)}
        />
        <DetailRow
          label={t("Control Position Front")}
          value={translateValue(t, product.controlPositionFront)}
        />
        <DetailRow
          label={t("Control Position Back")}
          value={translateValue(t, product.controlPositionBack)}
        />
        <DetailRow label={t("Chain Length")} value={getChainLength(product.ChainLength, t)} />
        <DetailRow label={t("Headrail")} value={translateValue(t, product.Headrail)} />
        <DetailRow label={t("Roll Type")} value={translateValue(t, product.RollType)} />
        <DetailRow label={t("Bracket Type")} value={translateValue(t, product.BracketType)} />
        <DetailRow label={t("Bracket Color")} value={translateValue(t, product.BracketColor)} />
        <DetailRow
          label={t("Metal Valance Color")}
          value={translateValue(t, product.MetalValanceColor)}
        />
        <DetailRow label={t("Hem Style")} value={translateValue(t, product.HemStyle)} />
        <DetailRow
          label={t("Bottom Bar Style")}
          value={translateValue(t, product.BottomBarStyle)}
        />
      </DetailSection>
      <AccessoriesSection product={product} isFa={isFa} t={t} />
    </>
  );
}

// Roman: 0097
function Details0097({ product, isFa, t }) {
  return (
    <>
      <DetailSection title={t("General Details")}>
        <DetailRow label={t("Fabric & Color")} value={getFabricColor(product, isFa)} />
        <DetailRow label={t("Mount Position")} value={getMount(product, t)} />
        <DetailRow label={t("Control Type")} value={getControlType(product, t)} />
        <DetailRow
          label={t("Motor Position")}
          value={translateValue(t, product.MotorPosition)}
        />
        <DetailRow label={t("Remote Name")} value={product.RemoteName} />
        <DetailRow
          label={t("Motor Channels")}
          value={getMotorChannels(product.MotorChannels)}
        />
        <DetailRow label={t("Lining")} value={translateValue(t, product.Lining)} />
        <DetailRow label={t("Features")} value={translateValue(t, product.Features)} />
        <DetailRow
          label={t("Control Position")}
          value={translateValue(t, product.ControlPosition)}
        />
        <DetailRow
          label={`${t("Left Shade")} ${t("Width")}`}
          value={product.LeftShadeWidth}
        />
        <DetailRow
          label={`${t("Right Shade")} ${t("Width")}`}
          value={product.RightShadeWidth}
        />
      </DetailSection>
      <AccessoriesSection product={product} isFa={isFa} t={t} />
    </>
  );
}

// Roller: 0324
function Details0324({ product, isFa, t }) {
  return (
    <>
      <DetailSection title={t("General Details")}>
        <DetailRow label={t("Fabric & Color")} value={getFabricColor(product, isFa)} />
        <DetailRow label={t("Mount Position")} value={getMount(product, t)} />
        <DetailRow label={t("Control Type")} value={getControlType(product, t)} />
        <DetailRow
          label={t("Motor Position")}
          value={translateValue(t, product.MotorPosition)}
        />
        <DetailRow label={t("Remote Name")} value={product.RemoteName} />
        <DetailRow
          label={t("Motor Channels")}
          value={getMotorChannels(product.MotorChannels)}
        />
        <DetailRow
          label={t("Control Position")}
          value={translateValue(t, product.ControlPosition)}
        />
        <DetailRow label={t("Chain Length")} value={getChainLength(product.ChainLength, t)} />
        <DetailRow label={t("Headrail")} value={translateValue(t, product.Headrail)} />
        <DetailRow label={t("Roll Type")} value={translateValue(t, product.RollType)} />
        <DetailRow label={t("Bracket Type")} value={translateValue(t, product.BracketType)} />
        <DetailRow label={t("Bracket Color")} value={translateValue(t, product.BracketColor)} />
        <DetailRow
          label={t("Metal Valance Color")}
          value={translateValue(t, product.MetalValanceColor)}
        />
        <DetailRow label={t("Hem Style")} value={translateValue(t, product.HemStyle)} />
        <DetailRow
          label={t("Bottom Bar Style")}
          value={translateValue(t, product.BottomBarStyle)}
        />
      </DetailSection>
      <AccessoriesSection product={product} isFa={isFa} t={t} />
    </>
  );
}

// Cellular / مشابه: 0326
function Details0326({ product, isFa, t }) {
  return (
    <>
      <DetailSection title={t("General Details")}>
        <DetailRow label={t("Mount Position")} value={getMount(product, t)} />
        <DetailRow
          label={t("Finished Length Type")}
          value={translateValue(t, getFinishedLengthType(product))}
        />
        <DetailRow
          label={t("Stack Position")}
          value={translateValue(t, product.StackPosition)}
        />
        <DetailRow
          label={t("Control Position")}
          value={translateValue(t, product.ControlPosition)}
        />
        <DetailRow
          label={t("Motor Position")}
          value={translateValue(t, product.MotorPosition)}
        />
        <DetailRow label={t("Control Type")} value={getControlType(product, t)} />
        <DetailRow label={t("Fabric & Color")} value={getFabricColor(product, isFa)} />
      </DetailSection>
      <AccessoriesSection product={product} isFa={isFa} t={t} />
    </>
  );
}

// 0327 و 0328 ساختار یکسان دارند
function Details0327And0328({ product, isFa, t }) {
  return (
    <>
      <DetailSection title={t("General Details")}>
        <DetailRow label={t("Fabric & Color")} value={getFabricColor(product, isFa)} />
        <DetailRow label={t("Mount Position")} value={getMount(product, t)} />
        <DetailRow label={t("Control Type")} value={getControlType(product, t)} />
        <DetailRow
          label={t("Motor Position")}
          value={translateValue(t, product.MotorPosition)}
        />
        <DetailRow label={t("Remote Name")} value={product.RemoteName} />
        <DetailRow
          label={t("Motor Channels")}
          value={getMotorChannels(product.MotorChannels)}
        />
        <DetailRow label={t("Lining")} value={translateValue(t, product.Lining)} />
        <DetailRow label={t("Features")} value={translateValue(t, product.Features)} />
        <DetailRow
          label={t("Control Position")}
          value={translateValue(t, product.ControlPosition)}
        />
        <DetailRow
          label={`${t("Left Shade")} ${t("Width")}`}
          value={product.LeftShadeWidth}
        />
        <DetailRow
          label={`${t("Right Shade")} ${t("Width")}`}
          value={product.RightShadeWidth}
        />
      </DetailSection>
      <AccessoriesSection product={product} isFa={isFa} t={t} />
    </>
  );
}

// 0303
function Details0303({ product, isFa, t }) {
  return (
    <>
      <DetailSection>
        <DetailRow label={t("Fabric & Color")} value={getFabricColor(product, isFa)} />
        <DetailRow label={t("Mount Position")} value={getMount(product, t)} />
        <DetailRow label={t("Control Type")} value={getControlType(product, t)} />
        <DetailRow
          label={t("Motor Position")}
          value={translateValue(t, product.MotorPosition)}
        />
        <DetailRow label={t("Remote Name")} value={product.RemoteName} />
        <DetailRow
          label={t("Motor Channels")}
          value={getMotorChannels(product.MotorChannels)}
        />
        <DetailRow
          label={t("Control Position")}
          value={translateValue(t, product.ControlPosition)}
        />
        <DetailRow label={t("Chain Length")} value={getChainLength(product.ChainLength, t)} />
        <DetailRow
          label={t("Metal Valance Style")}
          value={translateValue(t, product.MetalValanceStyle)}
        />
        <DetailRow
          label={t("Metal Valance Color")}
          value={translateValue(t, product.MetalValanceColor)}
        />
      </DetailSection>
      <AccessoriesSection product={product} isFa={isFa} t={t} />
    </>
  );
}

// اگر مدل ناشناخته بود، چند فیلد عمومی نشان بده
function DetailsFallback({ product, isFa, t }) {
  return (
    <>
      <DetailSection title={t("General Details")}>
        <DetailRow label={t("Mount Position")} value={getMount(product, t)} />
        <DetailRow label={t("Fabric & Color")} value={getFabricColor(product, isFa)} />
        <DetailRow label={t("Lining")} value={translateValue(t, product.Lining)} />
        <DetailRow label={t("Features")} value={translateValue(t, product.Features)} />
        <DetailRow
          label={t("Control Position")}
          value={translateValue(t, product.ControlPosition)}
        />
        <DetailRow label={t("Control Type")} value={getControlType(product, t)} />
        <DetailRow label={t("Panel Type")} value={translateValue(t, product.PanelTypeA)} />
        <DetailRow
          label={t("Hardware")}
          value={translateValue(t, getDraperyHardware(product, isFa))}
        />
      </DetailSection>
      <AccessoriesSection product={product} isFa={isFa} t={t} />
    </>
  );
}

// ---------- کامپوننت اصلی: فقط if / else if ----------

function OrderProductDetailsByModel({ product }) {
  const { t, i18n } = useTranslation();
  if (!product) return null;

  const isFa = i18n.language === "fa";
  const modelId = product.SewingModelId;
  const shared = { product, isFa, t };

  if (modelId === "0325") {
    return <Details0325 {...shared} />;
  } else if (modelId === "0326") {
    return <Details0326 {...shared} />;
  } else if (modelId === "0327") {
    return <Details0327And0328 {...shared} />;
  } else if (modelId === "0328") {
    return <Details0327And0328 {...shared} />;
  } else if (modelId === "0321") {
    return <Details0099And0321 {...shared} />;
  } else if (modelId === "0099") {
    return <Details0099And0321 {...shared} />;
  } else if (modelId === "0303") {
    return <Details0303 {...shared} />;
  } else if (modelId === "0097") {
    return <Details0097 {...shared} />;
  } else if (modelId === "0324") {
    return <Details0324 {...shared} />;
  } else {
    return <DetailsFallback {...shared} />;
  }
}

export default OrderProductDetailsByModel;
